import i18n from "../../i18n";
import fieldLabels from "../../data/mcaFieldLabels.json";
import { getMcaEntityFormSchema } from "./mcaEntityFormRegistry";

const STATIC_LABELS_BY_ENTITY = fieldLabels.staticLabelsByEntity;
const COMMON_FIELD_LABELS = fieldLabels.commonFieldLabels;
const EXTRA_FIELD_LABELS = fieldLabels.extraFieldLabels;
const FIELD_ZH_BY_PATH = fieldLabels.fieldZhByPath;
const ENGLISH_ZH_FALLBACK = fieldLabels.englishZhFallback;

const humanizeFieldPath = (fieldPath) =>
  fieldPath
    .split(".")
    .map((part) =>
      part
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    )
    .join(" / ");

export const extractSchemaFieldLabels = (schema) => {
  const labels = {};
  (schema?.layout?.fields ?? []).forEach((field) => {
    const name = field?.name;
    const label = field?.props?.label;
    if (name && typeof label === "string" && label.trim()) {
      labels[name] = label.trim();
    }
  });
  return labels;
};

const resolveEnglishLabel = (entityKey, fieldPath, schemaLabels) => {
  const staticLabels = STATIC_LABELS_BY_ENTITY[entityKey] ?? {};
  return (
    staticLabels[fieldPath] ??
    schemaLabels[fieldPath] ??
    EXTRA_FIELD_LABELS[fieldPath] ??
    COMMON_FIELD_LABELS[fieldPath] ??
    humanizeFieldPath(fieldPath)
  );
};

const zhT = () => i18n.getFixedT("zh-tw");

const resolveChineseLabel = (fieldPath, english) => {
  if (FIELD_ZH_BY_PATH[fieldPath]) return FIELD_ZH_BY_PATH[fieldPath];

  const t = zhT();
  const candidates = [
    english,
    humanizeFieldPath(fieldPath),
    COMMON_FIELD_LABELS[fieldPath],
    EXTRA_FIELD_LABELS[fieldPath],
  ].filter(Boolean);

  for (const candidate of candidates) {
    const translated = t(candidate);
    if (translated && translated !== candidate) return translated;
    if (ENGLISH_ZH_FALLBACK[candidate]) return ENGLISH_ZH_FALLBACK[candidate];
  }

  const leaf = fieldPath.split(".").pop();
  if (FIELD_ZH_BY_PATH[leaf]) return FIELD_ZH_BY_PATH[leaf];

  return ENGLISH_ZH_FALLBACK[english] ?? "—";
};

export const formatBilingualWatchFieldLabel = (english, chinese) => {
  if (chinese && chinese !== "—") return `${english} / ${chinese}`;
  return english;
};

export const getWatchFieldSelectOption = (entityKey, fieldPath) => {
  if (!fieldPath) return null;
  const [info] = getMcaWatchFieldExplanations(entityKey, [fieldPath]);
  if (!info) return { value: fieldPath, label: fieldPath };
  return {
    value: info.field,
    label: formatBilingualWatchFieldLabel(info.english, info.chinese),
  };
};

export const buildWatchFieldSelectOptions = (entityKey, fieldPaths = []) =>
  getMcaWatchFieldExplanations(entityKey, fieldPaths).map(({ field, english, chinese }) => ({
    value: field,
    label: formatBilingualWatchFieldLabel(english, chinese),
  }));

export const getMcaWatchFieldExplanations = (entityKey, fieldPaths = []) => {
  const schema = entityKey ? getMcaEntityFormSchema(entityKey, (key) => key) : null;
  const schemaLabels = extractSchemaFieldLabels(schema);

  return [...fieldPaths]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .map((field) => {
      const english = resolveEnglishLabel(entityKey, field, schemaLabels);
      return {
        field,
        english,
        chinese: resolveChineseLabel(field, english),
      };
    });
};
